import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { GamblingGameProps } from './GamblingHall';
import styles from './DiceGame.module.css';

const ROLL_ANIM_MS = 1400;

const FACE_DOTS: Record<number, [number, number][]> = {
  1: [[50, 50]],
  2: [[25, 25], [75, 75]],
  3: [[25, 25], [50, 50], [75, 75]],
  4: [[25, 25], [75, 25], [25, 75], [75, 75]],
  5: [[25, 25], [75, 25], [50, 50], [25, 75], [75, 75]],
  6: [[25, 20], [75, 20], [25, 50], [75, 50], [25, 80], [75, 80]],
};

const DieFace: React.FC<{ value: number; rolling: boolean; settled: boolean }> = ({ value, rolling, settled }) => (
  <div className={`${styles.die} ${rolling ? styles.dieRolling : ''} ${settled ? styles.dieSettled : ''}`}>
    {(FACE_DOTS[value] ?? FACE_DOTS[1]).map(([x, y], i) => (
      <span key={i} className={`${styles.pip} ${styles.pipVisible}`} style={{ left: `${x}%`, top: `${y}%` }} />
    ))}
  </div>
);

const DiceGame: React.FC<GamblingGameProps> = ({ mercenaryId, silver, remaining, nextRollAt, onResult }) => {
  const [bet, setBet] = useState(10);
  const [rolling, setRolling] = useState(false);
  const [diceValues, setDiceValues] = useState<[number, number]>([1, 1]);
  const [result, setResult] = useState<{ win: boolean; message: string } | null>(null);
  const [cooldownDisplay, setCooldownDisplay] = useState<string | null>(null);
  const shuffleRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const dismissRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cooldown ticker
  useEffect(() => {
    if (!nextRollAt) { setCooldownDisplay(null); return; }
    const tick = () => {
      const msLeft = nextRollAt.getTime() - Date.now();
      if (msLeft <= 0) { setCooldownDisplay(null); return; }
      const s = Math.ceil(msLeft / 1000);
      const m = Math.floor(s / 60);
      setCooldownDisplay(`${m > 0 ? m + 'm ' : ''}${s % 60}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [nextRollAt]);

  const scheduleResultDismiss = useCallback(() => {
    if (dismissRef.current) clearTimeout(dismissRef.current);
    dismissRef.current = setTimeout(() => setResult(null), 10000);
  }, []);

  const rand6 = () => Math.floor(Math.random() * 6) + 1;
  const canRoll = !rolling && !cooldownDisplay && remaining > 0 && bet >= 1;

  const handlePlay = async () => {
    if (!canRoll) return;
    setRolling(true);
    setResult(null);

    shuffleRef.current = setInterval(() => {
      setDiceValues([rand6(), rand6()]);
    }, 80);

    try {
      const res = await axios.post('/api/tavern/play-dice', { mercenaryId, bet });
      const roll1 = res.data.roll1 ?? rand6();
      const roll2 = res.data.roll2 ?? rand6();

      setTimeout(() => {
        if (shuffleRef.current) { clearInterval(shuffleRef.current); shuffleRef.current = null; }
        setDiceValues([roll1, roll2]);
        setRolling(false);
        setResult({ win: res.data.win ?? res.data.message?.includes('+'), message: res.data.message });
        onResult(res.data.newSilver, res.data.remaining, res.data.nextRollAt ? new Date(res.data.nextRollAt) : null);
        scheduleResultDismiss();
      }, ROLL_ANIM_MS);
    } catch (err: any) {
      if (shuffleRef.current) { clearInterval(shuffleRef.current); shuffleRef.current = null; }
      setRolling(false);
      setResult({ win: false, message: err?.response?.data?.message || err?.response?.data?.error || 'Failed to roll.' });
      scheduleResultDismiss();
    }
  };

  return (
    <div className={styles.diceGame}>
      <p className={styles.description}>Roll two dice - land exactly 7 to win x5 your bet.</p>

      <div className={styles.diceArea}>
        <DieFace value={diceValues[0]} rolling={rolling} settled={!rolling && result !== null} />
        <DieFace value={diceValues[1]} rolling={rolling} settled={!rolling && result !== null} />
      </div>

      <div className={styles.betControls}>
        <label>Bet:</label>
        <input
          type="number"
          min={1}
          max={Math.min(silver, remaining)}
          value={bet || ''}
          placeholder="1"
          onChange={e => { const raw = e.target.value; setBet(raw === '' ? 0 : Math.min(silver, remaining, parseInt(raw) || 0)); }}
          className={styles.plainInput}
        />
        <button onClick={handlePlay} className={styles.rollButton} disabled={!canRoll}>
          {rolling ? 'Rolling…' : cooldownDisplay ? `Wait ${cooldownDisplay}` : remaining === 0 ? 'Limit reached' : 'Roll Dice'}
        </button>
      </div>

      {result && (
        <div className={result.win ? styles.resultSuccess : styles.resultError}>
          {result.message}
        </div>
      )}
    </div>
  );
};

export default DiceGame;
